import jwt_decode from 'jwt-decode';
import { useMemo } from 'react';
import { useAuth as useNativeAuth } from 'react-oidc-context';

import { AuthUser } from '@/features/auth';
import { useDivisionQuery, useUserQuery } from '@/generated/graphql';
import { signOutRedirect } from '@/providers/app';

const getCognitoUserName = (idToken?: string) => {
  if (!idToken) {
    return null;
  }
  const decoded: any = jwt_decode(idToken);
  return decoded['cognito:username'];
};

export const useAuth = () => {
  const auth = useNativeAuth();
  const userCode = getCognitoUserName(auth.user?.id_token);

  const { data } = useUserQuery(
    { id: window.btoa('User:' + userCode) },
    { suspense: true, enabled: auth.isAuthenticated }
  );
  const { data: divisionData } = useDivisionQuery(
    { id: window.btoa('Division:' + data?.user?.divisionCode) },
    { suspense: true, enabled: auth.isAuthenticated }
  );
  const user: AuthUser | undefined = useMemo(() => {
    if (auth.isAuthenticated && data?.user) {
      return {
        id: data.user.id,
        userCode: data.user.userCode,
        userName: data.user.userName,
        userType: data.user.userType,
        divisionCode: data.user.divisionCode ?? '',
        subDivision1: data.user.subDivision1 ?? '',
        subDivision2: data.user.subDivision2 ?? '',
        subDivision3: data.user.subDivision3 ?? '',
        subDivision4: data.user.subDivision4 ?? '',
        divisionCodes: [
          data.user.divisionCode ?? '',
          data.user.subDivision1 ?? '',
          data.user.subDivision2 ?? '',
          data.user.subDivision3 ?? '',
          data.user.subDivision4 ?? '',
        ].filter(Boolean),
        divisionName: divisionData?.division?.divisionName ?? '',
        email: data.user.email ?? '',
        superUser: data.user.superUser ?? '',
        priorityWarehouseCode: data.user.priorityWarehouseCode ?? null,
        approvalPrivilege: data.user.approvalPrivilege ?? '',
        trustInventoryApprovalPrivilege: data.user.trustInventoryApprovalPrivilege,
        role: 'ADMIN',
      };
    }
  }, [auth.isAuthenticated, data?.user, divisionData?.division?.divisionName]);

  return {
    user,
    auth: auth,
    logout: async () => {
      signOutRedirect();
    },
    refetchUser: () => {},
  };
};
